// src/components/AugustJourney.js
// August "Journey" screen — warm gold port of ~/Downloads/magic-warm-today-journey.html (Journey half).
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import AugustTexture from './AugustTexture'
import PageHeader from './ui/PageHeader'
import './AugustJourney.css'

// Original lines — safe to port (not the copyrighted reference lyrics used elsewhere).
const WALLPAPER_LINES = [
  "Twenty eight days and each one a little different",
  "Kept the streak alive through the busy week",
  "Week one felt slow and then it did not",
  "Looked back at day one and barely recognized it",
  "The pages are filling up faster than I thought",
  "Small things add up that is the whole idea",
  "Halfway soon and I do not want it to end",
  "The habit found me before I found it",
  "Some days three things some days ten",
  "Read last week back and smiled the whole time",
  "A quarter of the way and counting",
  "Kept going even on the gray days",
  "The notebook knows me better now",
  "Every stamp another small proof",
  "What gets written down gets remembered",
  "Twenty one more mornings to go",
  "It is the noticing that changes you",
  "One week down and lighter already",
  "The days keep arriving and I keep writing",
  "One small thank you at a time",
]

const RINGS = [
  { width: 80, height: 80, top: 40, left: -22, opacity: 0.55 },
  { width: 58, height: 58, bottom: 70, right: -12, opacity: 0.4 },
]

const STATIC_SPLATS = [
  { width: 12, height: 10, top: 88, right: 26, opacity: 0.38, borderRadius: '60% 40% 55% 45%' },
  { width: 5,  height: 5,  top: 100, right: 40, opacity: 0.28, borderRadius: '50%' },
  { width: 14, height: 11, bottom: 170, left: 18, opacity: 0.4, borderRadius: '55% 45% 48% 52%' },
]

const RING_RADIUS = 88
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

const WEEK_NAMES = ['Week one', 'Week two', 'Week three', 'Week four']
const WEEK_SUBTITLES = ['The Beginning', 'Building the Habit', 'Keep It Burning', 'The Becoming']

export default function AugustJourney({ nextStation = 1, completedCount = 0, onOpenTracklist, augustTheme, onAugustThemeChange }) {
  const reduced = useRef(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  ).current

  const [show, setShow] = useState({
    ring: reduced, ringNum: reduced, ringOf: reduced, streak: reduced,
    w1: reduced, w2: reduced, w3: reduced, w4: reduced, whisper: reduced,
  })

  useEffect(() => {
    if (reduced) return
    const timers = []
    const after = (ms, fn) => timers.push(setTimeout(fn, ms))
    after(300,  () => setShow(s => ({ ...s, ring: true })))
    after(500,  () => setShow(s => ({ ...s, ringNum: true })))
    after(700,  () => setShow(s => ({ ...s, ringOf: true })))
    after(1100, () => setShow(s => ({ ...s, streak: true })))
    after(1300, () => setShow(s => ({ ...s, w1: true })))
    after(1450, () => setShow(s => ({ ...s, w2: true })))
    after(1600, () => setShow(s => ({ ...s, w3: true })))
    after(1750, () => setShow(s => ({ ...s, w4: true })))
    after(2100, () => setShow(s => ({ ...s, whisper: true })))
    return () => timers.forEach(clearTimeout)
  }, [reduced])

  // Ring numeral centering — same mechanism as the writing-form ring (Journal.js): measures
  // the glyph's actual rendered ink bounds via SVG getBBox() and corrects to true center,
  // rather than a hand-tuned offset. That's the direct replacement for this ring's old
  // translateX(-0.06em) hack, which was calibrated for Signatural's specific bearing
  // asymmetry and would have been meaningless (probably wrong) for Northwell — a magic number
  // tuned for one font has no relationship to a different font's metrics.
  const ringNumRef = useRef(null)
  const [ringNumOffset, setRingNumOffset] = useState({ x: 0, y: 0 })
  useLayoutEffect(() => {
    const measure = () => {
      if (!ringNumRef.current) return
      try {
        const bbox = ringNumRef.current.getBBox()
        setRingNumOffset({ x: 85 - (bbox.x + bbox.width / 2), y: 50 - (bbox.y + bbox.height / 2) })
      } catch (e) { /* not laid out yet — next measure() call catches it */ }
    }
    measure()
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure)
    }
  }, [nextStation])

  const week = Math.min(4, Math.max(1, Math.ceil(nextStation / 7)))

  // Real streak concept exists (calcCityStreak in Dashboard.js) but it walks the full
  // per-day entries list looking for the first gap from day 1, which isn't data App.js
  // already loads for this screen. Using completedCount as a proxy — identical to the
  // real streak as long as no earlier day was skipped, which is the expected common case.
  const streak = completedCount

  // status: 'current' wins on the week containing nextStation even if its count is 0 (just
  // started) or 7 (just finished) — count alone can't distinguish "this week" from "done"/
  // "not started yet" at its boundaries, but the week index can.
  const currentWeekIdx = week - 1
  const weeks = WEEK_NAMES.map((name, i) => {
    const weekStart = i * 7 + 1
    const count = Math.min(Math.max(completedCount - weekStart + 1, 0), 7)
    const status = i === currentWeekIdx ? 'current' : count >= 7 ? 'done' : 'upcoming'
    return { name, count, status, subtitle: WEEK_SUBTITLES[i] }
  })

  const ringOffset = RING_CIRCUMFERENCE * (1 - Math.min(completedCount, 28) / 28)

  return (
    <div className="aug-journey">
      <AugustTexture variant="corner" wallpaperLines={WALLPAPER_LINES} rings={RINGS} splats={STATIC_SPLATS} lineCount={24} />

      <div className="aug-journey-screen">
        <PageHeader
          eyebrow={`Week ${week}`}
          title="Journey"
          descriptor={`Day ${nextStation} of 28${streak > 0 ? ` • ${streak}-day streak` : ''}`}
          augustTheme={augustTheme} onAugustThemeChange={onAugustThemeChange}
        />

        <div className="aug-journey-ring-wrap">
          <svg className="progress" viewBox="0 0 196 196" width="196" height="196">
            <circle className="aug-journey-ring-track" cx="98" cy="98" r={RING_RADIUS} fill="none" strokeWidth="7" />
            <circle
              className="aug-journey-ring-fill"
              cx="98" cy="98" r={RING_RADIUS} fill="none" strokeWidth="7"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={show.ring ? ringOffset : RING_CIRCUMFERENCE}
            />
          </svg>
          <div className="aug-journey-ring-center">
            <div className={`aug-journey-ring-day-lbl${show.ringNum ? ' show' : ''}`}>Day</div>
            <svg className="aug-journey-ring-num-svg" viewBox="0 0 170 100" width="170" height="100">
              <g transform={`translate(${ringNumOffset.x}, ${ringNumOffset.y})`}>
                <text
                  ref={ringNumRef}
                  x="85" y="50"
                  textAnchor="middle" dominantBaseline="middle"
                  className={`aug-journey-ring-num${show.ringNum ? ' show' : ''}`}
                >
                  {nextStation}
                </text>
              </g>
            </svg>
            <div className={`aug-journey-ring-of${show.ringOf ? ' show' : ''}`}>of 28</div>
          </div>
          <div className="aug-journey-ring-splat" />
        </div>

        {streak > 0 && (
          <div className={`aug-journey-streak${show.streak ? ' show' : ''}`}>
            {streak}-day streak &mdash; keep it burning
          </div>
        )}

        {/* Vertical timeline — replaces the old settings-table week list (progress bars +
            "N/7" text) with a single connected line of four week-nodes, per the reference
            redesign mock. Each node is a checkmark (done), a gold-outlined active ring
            (current), or an empty ring (upcoming); each week's 7 days render as dots instead
            of a bar, so the whole 28-day arc reads as one continuous journey rather than a
            data table. */}
        <div className="aug-journey-tl-intro">
          <div className="aug-journey-tl-eyebrow">The Journey So Far</div>
          <p className="aug-journey-tl-intro-sub">Four weeks. Twenty eight days. One thank-you at a time.</p>
        </div>

        <div className="aug-journey-timeline">
          {weeks.map((w, i) => (
            <div
              key={w.name}
              className={`aug-journey-tl-row${w.status === 'current' ? ' current' : ''}${show[`w${i + 1}`] ? ' show' : ''}`}
            >
              <div className="aug-journey-tl-node-col">
                <div className={`aug-journey-tl-node ${w.status}`}>
                  {w.status === 'done' && (
                    <svg width="12" height="10" viewBox="0 0 14 11" fill="none">
                      <path d="M1 5.5L5 9.5L13 1.5" stroke="var(--aug-cta-text)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                  {w.status === 'current' && <span className="aug-journey-tl-node-dot" />}
                </div>
                {i < weeks.length - 1 && <div className="aug-journey-tl-line" />}
              </div>
              <div className="aug-journey-tl-card">
                <div className="aug-journey-tl-week-label">Week {i + 1}</div>
                <div className="aug-journey-tl-subtitle">{w.subtitle}</div>
                <div className="aug-journey-tl-dots-row">
                  <div className="aug-journey-tl-dots">
                    {Array.from({ length: 7 }, (_, d) => (
                      <span key={d} className={`aug-journey-tl-dot${d < w.count ? ' filled' : ''}`} />
                    ))}
                  </div>
                  <span className="aug-journey-tl-count">{w.count}/7</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="aug-journey-tl-legend">
          <span><span className="aug-journey-tl-dot filled" /> Completed</span>
          <span><span className="aug-journey-tl-node-mini current" /> Current</span>
          <span><span className="aug-journey-tl-dot" /> Upcoming</span>
        </div>

        <button className={`aug-journey-tracklist-link${show.whisper ? ' show' : ''}`} onClick={onOpenTracklist}>View tracklist →</button>

        <div className={`aug-journey-whisper${show.whisper ? ' show' : ''}`}>One small thank-you at a time</div>
      </div>
    </div>
  )
}
